import { Page, PdlcStep } from 'components/framework/models';
import { type RecordId } from './shared-types';
import { type AhaEventMap } from 'global_events';
import { type DocumentTypeEnum_value } from 'components/framework/models/generated/types';
import AhaDrawer from './drawer';
import { createPage, findOrCreatePage, openPageNew } from './util/fetchPages';
import { createRecordLink } from './record_links_creator';

type PdlcNote = {
  type: PdlcNoteType;
  recordLink: PdlcLinkType;
};

type PdlcNoteType = 'Instructions' | 'Meetings';
type PdlcLinkType = '100' | '101';

export const PdlcNotes: Record<PdlcNoteType, PdlcNote> = {
  Instructions: {
    type: 'Instructions',
    recordLink: '100',
  },
  Meetings: {
    type: 'Meetings',
    recordLink: '101',
  },
};

class PdlcStepResources {
  stepId: RecordId;
  projectId: RecordId;
  templateModalPath: string;

  constructor(stepId: RecordId, projectId: RecordId) {
    this.stepId = stepId;
    this.projectId = projectId;
  }

  async initialize(templateModalPath: string) {
    this.templateModalPath = templateModalPath;
    this.setupInsertDocument();
  }

  setupInsertDocument() {
    const handler = async (event: AhaEventMap['aha.insert-document']) => {
      const { detail } = event;

      if (
        String(this.stepId) === detail.parentId &&
        detail.ctaSource?.slice(0, 4) === 'PDLC'
      ) {
        event.stopPropagation();
        event.stopImmediatePropagation();

        const pdlcNoteType = detail.ctaSource.slice(5) as PdlcNoteType;

        if (detail.documentType === 'TEMPLATE') {
          await this.createPageInFolderFromTemplate(pdlcNoteType);
        } else if (
          detail.documentType === 'INTERNAL_LINK' ||
          detail.documentType === 'EXTERNAL_LINK'
        ) {
          await this.createPageLink(pdlcNoteType, detail.documentType);
        } else {
          await this.createPageInFolder(detail.documentType, pdlcNoteType);
        }
      }
    };

    document.addEventListener('aha.insert-document', handler);

    document.addEventListener(
      'page:load',
      () => document.removeEventListener('aha.insert-document', handler),
      { once: true }
    );
  }

  async createPageInFolder(
    documentType: DocumentTypeEnum_value,
    pdlcNoteType: PdlcNoteType
  ) {
    const parentPage = await this.createParentFolder(pdlcNoteType);
    const page = await createPage(
      null,
      parentPage.id,
      documentType,
      this.projectId
    );
    await this.createPageRecordLink(page, pdlcNoteType);
    this.openDrawer(`/pages/${page.id}`);
  }

  async createParentFolder(pdlcNoteType: PdlcNoteType) {
    const parentFolder = await findOrCreatePage(
      pdlcNoteType,
      null,
      Page.DOCUMENT_TYPE_NOTE,
      this.projectId
    );

    return parentFolder;
  }

  async createPageLink(
    pdlcNoteType: PdlcNoteType,
    documentType: DocumentTypeEnum_value
  ) {
    const parentPage = await this.createParentFolder(pdlcNoteType);

    openPageNew(this.projectId, parentPage, documentType, {
      pdlc_step_id: this.stepId,
    });
  }

  async createPageInFolderFromTemplate(pdlcNoteType: PdlcNoteType) {
    const record = await PdlcStep.select('id', 'name').find(this.stepId);
    const parentPage = await this.createParentFolder(pdlcNoteType);

    await $.ajax({
      url: this.templateModalPath,
      type: 'GET',
      dataType: 'script',
      data: {
        template_type: 'all',
        parent_id: parentPage.id,
        after_view: 'open_pdlc_resource_drawer',
        record_id: record.id,
        record_name: record.name,
        record_type: 'Pdlc::Step',
      },
    });
  }

  async createPageRecordLink(page: Page, pdlcNoteType?: PdlcNoteType) {
    if (!pdlcNoteType) {
      const { name } = await Page.select('name').find(
        page.parentId as RecordId
      );
      pdlcNoteType = name as PdlcNoteType;
    }

    return createRecordLink(
      { id: this.stepId, type: 'Pdlc::Step' },
      { id: page.id, type: 'Page' },
      PdlcNotes[pdlcNoteType].recordLink
    );
  }

  openDrawer(recordPath: string) {
    // @ts-ignore
    $('#modal-window').modal('hide');
    AhaDrawer.showUrl(recordPath);
  }
}

export default PdlcStepResources;
